import React, { useState } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Library } from 'lucide-react';
import FormBuilder from './FormBuilder';
import TemplateLibrary from './TemplateLibrary';

const AppraisalBuilder = () => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState(null);
  const [isImportPreview, setIsImportPreview] = useState(false);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);

  // Called when user clicks "Create New Template"
  const handleCreateNew = (templateType) => {
    setEditingTemplate(null);
    setSelectedTemplateType(templateType);
    setIsImportPreview(false);
    setIsBuilderOpen(true);
  };

  // Called when user clicks "Edit" on existing template
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setSelectedTemplateType(template.type); // "Appraisal", "Exit Interview", or "PIP"
    setIsImportPreview(false);
    setIsBuilderOpen(true);
  };

  // Called after importing an Excel file - opens the create form pre-filled
  // with the parsed (unsaved) template so the user can review before saving.
  const handleImportPreview = (template) => {
    setEditingTemplate(template); // draft: has data but no _id
    setSelectedTemplateType(template.type);
    setIsImportPreview(true);
    setIsBuilderOpen(true);
  };

  // Called when template is saved - triggers library refresh
  const handleTemplateSaved = () => {
    setIsBuilderOpen(false);
    setEditingTemplate(null);
    setSelectedTemplateType(null);
    setIsImportPreview(false);
    // Trigger library refresh
    setLibraryRefreshKey(prev => prev + 1);
  };

  // Called to close builder without saving
  const handleCloseBuilder = () => {
    setIsBuilderOpen(false);
    setEditingTemplate(null);
    setSelectedTemplateType(null);
    setIsImportPreview(false);
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-foreground" data-testid="appraisal-builder-heading">
          Appraisal Builder
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage templates and build custom appraisal forms.
        </p>
      </div>

      {/* Template Library - Always visible */}
      <TemplateLibrary
        key={libraryRefreshKey}
        onCreateNew={handleCreateNew}
        onEditTemplate={handleEditTemplate}
        onImportPreview={handleImportPreview}
      />

      {/* Form Builder Modal */}
      <Dialog open={isBuilderOpen} onOpenChange={(open) => !open && handleCloseBuilder()}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-[95vh] p-0 overflow-hidden flex flex-col">
          {/* Header with close button */}
          <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
            <h2 className="text-xl font-semibold">
              {editingTemplate && !isImportPreview ? 'Edit Template' : 'Create New Template'}
            </h2>
          </div>

          {/* FormBuilder content - scrollable area */}
          <div className="flex-1 overflow-y-auto p-6 no-scrollbar">
            <FormBuilder
              templateType={selectedTemplateType}
              editingTemplate={editingTemplate}
              onTemplateSaved={handleTemplateSaved}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppraisalBuilder;